import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { formatMoney } from "@/lib/money";
import { Loader2, TrendingUp } from "lucide-react";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type Range = "today" | "week" | "month";

interface Tx {
  id: string;
  amount_cents: number;
  created_at: string;
}

const rangeStart = (r: Range) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (r === "week") d.setDate(d.getDate() - 6);
  if (r === "month") d.setDate(d.getDate() - 29);
  return d;
};

const MerchantDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>("today");
  const [txs, setTxs] = useState<Tx[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    supabase
      .from("transactions")
      .select("id, amount_cents, created_at")
      .eq("receiver_id", user.id)
      .gte("created_at", rangeStart(range).toISOString())
      .order("created_at", { ascending: true })
      .then(({ data }) => {
        setTxs((data as Tx[] | null) ?? []);
        setLoading(false);
      });
  }, [user, range]);

  const total = txs.reduce((s, t) => s + t.amount_cents, 0);
  const count = txs.length;
  const avg = count ? Math.round(total / count) : 0;

  const chartData = useMemo(() => {
    if (range === "today") {
      // 24 hourly buckets
      const buckets = Array.from({ length: 24 }, (_, h) => ({
        label: `${h}`,
        cents: 0,
      }));
      txs.forEach((t) => {
        const h = new Date(t.created_at).getHours();
        buckets[h].cents += t.amount_cents;
      });
      return buckets;
    }
    const days = range === "week" ? 7 : 30;
    const buckets: { label: string; cents: number; key: string }[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      buckets.push({
        label: d.toLocaleDateString(undefined, {
          month: range === "month" ? undefined : "short",
          day: "numeric",
        }),
        key: d.toISOString().slice(0, 10),
        cents: 0,
      });
    }
    const map = new Map(buckets.map((b) => [b.key, b]));
    txs.forEach((t) => {
      const key = new Date(t.created_at).toISOString().slice(0, 10);
      const b = map.get(key);
      if (b) b.cents += t.amount_cents;
    });
    return buckets;
  }, [txs, range]);

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Earnings</h1>
      </BrandHeader>

      {/* Range tabs */}
      <div className="px-5 -mt-5">
        <div className="bg-background rounded-2xl shadow-card p-1.5 flex">
          {(["today", "week", "month"] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium capitalize transition-smooth ${
                range === r
                  ? "bg-primary text-primary-foreground shadow-soft"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 mt-6 space-y-4 flex-1 animate-fade-in-up">
        {/* Total card */}
        <div className="rounded-2xl bg-card-gradient text-primary-foreground p-5 shadow-card">
          <p className="text-xs uppercase tracking-wider opacity-80">
            Total · {range === "today" ? "today" : range === "week" ? "last 7 days" : "last 30 days"}
          </p>
          <p className="font-display text-4xl font-extrabold mt-1">{formatMoney(total)}</p>
          <div className="flex items-center gap-1.5 text-xs opacity-90 mt-2">
            <TrendingUp className="h-3.5 w-3.5" />
            {count} {count === 1 ? "payment" : "payments"} · avg {formatMoney(avg)}
          </div>
        </div>

        {/* Chart */}
        <div className="rounded-2xl border border-border bg-card p-4">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">
            {range === "today" ? "By hour" : "By day"}
          </h3>
          <div className="h-44">
            {loading ? (
              <div className="h-full grid place-items-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    interval={range === "month" ? 4 : range === "week" ? 0 : 3}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(v) => `$${Math.round(v / 100)}`}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                  />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--muted))" }}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid hsl(var(--border))",
                      background: "hsl(var(--background))",
                      fontSize: 12,
                    }}
                    formatter={(v: number) => [formatMoney(v), "Earnings"]}
                  />
                  <Bar dataKey="cents" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Payments" value={count.toString()} />
          <StatCard label="Average" value={formatMoney(avg)} />
          <StatCard
            label={range === "today" ? "Best hour" : "Best day"}
            value={(() => {
              if (!chartData.length) return "—";
              const best = chartData.reduce((a, b) => (b.cents > a.cents ? b : a));
              return best.cents > 0 ? best.label : "—";
            })()}
          />
        </div>
      </div>

      <div className="safe-bottom" />
    </MobileShell>
  );
};

const StatCard = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl bg-secondary/60 p-3 text-center">
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-display font-bold text-sm mt-1 truncate">{value}</p>
  </div>
);

export default MerchantDashboard;
